import React, { useEffect } from 'react';
import { BlankModal, EmptyState, ModalBody } from '@cad-arc';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { logInfo } from '../monitoring/log';
import { shouldBlockOutOfDateClient } from './should-block-out-of-date-client';

/**
 * Full-screen, undismissable modal shown to desktop users running a version below the
 * minimum supported one, once their agency has been switched over to enforced deprecation.
 *
 * Distinct from {@link DeprecationBanner}: the banner is a dismissable warning that renders
 * as soon as the client is out of date, while this modal is a hard block that only renders
 * once `legacyAppDeprecation` is turned on for the department. Banner first, block later,
 * both driven by the same `parsedClientApp.outOfDate` threshold.
 *
 * Web is deliberately excluded — cad-next on the web is the migration destination, so there
 * is nowhere better to send those users.
 *
 * Rendered declaratively rather than through the `src/modals/` registry, because that
 * registry holds one modal at a time and any other modal opening would evict this one.
 *
 * Logs once when it starts blocking, so Datadog shows who was locked out. The backend logs
 * the same message when it serves the bootstrap that leads here.
 */
export function DeprecationBlockModal(): React.ReactElement | null {
    const { legacyAppDeprecation } = useEnabledFeatures();
    const { isElectron, isFirstResponder } = useApplicationType();
    const { parsedClientApp } = useBootstrapState();

    const isBlocked = shouldBlockOutOfDateClient({
        legacyAppDeprecation,
        isElectron,
        outOfDate: parsedClientApp?.outOfDate,
    });
    const appName = parsedClientApp?.appName;
    const version = parsedClientApp?.version;

    useEffect(
        function logBlockShown() {
            if (isBlocked) {
                logInfo('Client app blocked by deprecation', {
                    deprecation_block: {
                        app: appName,
                        platform: 'desktop',
                        version,
                        outcome: 'block',
                    },
                });
            }
        },
        [isBlocked, appName, version]
    );

    // Narrow parsedClientApp explicitly — isBlocked is computed through the optional chain,
    // so TypeScript can't infer non-nullness from it.
    if (!parsedClientApp || !isBlocked) {
        return null;
    }

    // Always the fully-qualified product name in user-facing copy — never bare
    // "CAD" or "First Responder".
    const appLabel = isFirstResponder ? 'Mark43 First Responder' : 'Mark43 CAD';

    return (
        <BlankModal isOpen size="md" isCloseable={false} onClose={() => undefined}>
            <ModalBody>
                <EmptyState
                    visual="Download"
                    headingLevel="h2"
                    title={`A newer version of ${appLabel} is required`}
                    subtitle={`${appLabel} version ${parsedClientApp.version} is no longer supported. Please contact your agency's IT department to have the current version of ${appLabel} installed.`}
                />
            </ModalBody>
        </BlankModal>
    );
}
